import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { presetsApi } from "../../api/presets";
import { trackEvent } from "../../lib/analytics";
import { useLoopPlayer } from "../../lib/useLoopPlayer";
import { isSelectionVisible } from "../../lib/previewSelection";
import {
  defaultFilters,
  parseKitsParams,
  serializeKitsParams,
  summarizeFilters,
  noResultsBody,
  emptyStateActions,
  isAnyFilterActive,
} from "../../lib/kitsBrowse";
import { useAuth } from "../../hooks/useAuth";
import { downloadKit } from "../../lib/kitDownload";
import ViewTabs from "./controls/ViewTabs";
import GenreChips from "./controls/GenreChips";
import BpmControl from "./controls/BpmControl";
import SearchField from "./controls/SearchField";
import ResultsLine from "./controls/ResultsLine";
import KitsSkeleton from "./KitsSkeleton";
import KitsEmptyState from "./KitsEmptyState";
import RatingStars from "./RatingStars";
import FavoriteButton from "./FavoriteButton";
import CopyLinkButton from "./CopyLinkButton";
import KitWaveform from "./KitWaveform";
import PreviewKnob from "./PreviewKnob";
import { assignRowColors } from "../../lib/kitColor";

const PAGE_SIZE = 48;

// Public catalogue of shared kits + patterns. Filters (genre, BPM, search)
// narrow the set; sort orders it; the URL is the single source of truth for
// both (design doc "filter-sort-design.md", sections 0-2). See that document
// for the full behavioural spec -- this file implements it, it doesn't
// restate it.
export default function KitsCatalogue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const spString = searchParams.toString();
  // Keyed on the STRING on purpose (design section 10.3: "the fetch effect
  // keys on searchParams.toString()"), so `filters` is a stable reference
  // for as long as spString doesn't change -- react-router hands back a new
  // URLSearchParams object on every render, so depending on `searchParams`
  // itself would recompute (and, via the effect below, refetch) every time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => parseKitsParams(searchParams), [spString]);

  // `loading` matters as much as `user`: on a cold load of /kits?view=favorites
  // the auth check has not resolved yet, and firing the public list first would
  // paint the whole catalogue for a beat before swapping it for the favourites.
  const { user, loading: authLoading } = useAuth();
  // Signed out, the favourites view does not exist -- fall back to the public
  // catalogue rather than 401ing, so a stale bookmark still shows something.
  const showFavorites = filters.view === "favorites" && Boolean(user);
  // `view` is a client-side concern -- it picks the endpoint. Spreading it into
  // the catalogue query would put a meaningless `view=` on every request.
  // eslint-disable-next-line no-unused-vars
  const { view: _view, ...listFilters } = filters;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [phase, setPhase] = useState("loading"); // "loading" | "ready" | "error"
  const [refetching, setRefetching] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // hasLoadedOnce distinguishes the FIRST load (full skeleton) from a
  // refetch (dim the existing grid instead) -- design section 9.1.
  const hasLoadedOnce = useRef(false);
  // genRef is the "alive" guard, generalised to also cover loadMore: every
  // fresh filter/sort fetch bumps it, and any in-flight response (the main
  // effect's OR a loadMore's) whose generation has gone stale is dropped.
  // Kept alongside the AbortController below rather than instead of it --
  // they catch overlapping but not identical cases (a genuinely cancelled
  // request vs. one that raced and lost) -- per this task's own brief.
  const genRef = useRef(0);

  // Inline catalogue preview: ONE shared player for the whole grid, never
  // one AudioContext per card (useLoopPlayer's own comment explains why
  // that would be wrong - overlapping audio, and browsers cap how many
  // live contexts can exist at once). `activePreview` is "the card the
  // visitor last clicked play on"; the hook's `url` follows it, so
  // selecting a different card stops whatever was playing (see the hook's
  // url-change effect) before the new one starts. Unmounting this page (the
  // visitor opens a kit, or navigates elsewhere) tears the hook down too,
  // which closes its AudioContext - so audio never bleeds into another
  // screen without any extra cleanup code needed here.
  const [activePreview, setActivePreview] = useState(null); // { id, url } | null
  const player = useLoopPlayer(activePreview?.url ?? null);

  // Fires only when the SELECTED card changes, not on every isPlaying/
  // isLoading flip of the current one (pausing/resuming the same card goes
  // through player.toggle() directly, in handleTogglePreview). By the time
  // this effect runs, `player.play` already closes over the new url - it
  // was created in the same render that updated activePreview.
  useEffect(() => {
    if (activePreview) player.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreview?.id]);

  // Stuck-state guard: if the selected card scrolls out of the rendered
  // results (a genre/BPM/search/sort change settles on a set that no longer
  // includes it), stop the preview instead of leaving it playing with its
  // Pause control gone from the DOM and no way left to stop it. Keyed on
  // `items` itself, which the fetch effect above only ever reassigns once a
  // request SETTLES -- a refetch dims the existing grid via `refetching`
  // rather than clearing `items` -- so this never fires mid-keystroke while
  // a debounced search request is still in flight, only once new results
  // actually land, and only if the id genuinely isn't among them.
  // `activePreview` is a real dependency too, but reading it here never
  // fights the normal play/pause path: toggling the SAME card (see
  // handleTogglePreview below) only flips the hook's isPlaying/isLoading,
  // it never reassigns `activePreview`, so this effect is a no-op on every
  // ordinary play/pause and does not re-fire on every render.
  useEffect(() => {
    if (!activePreview) return;
    if (isSelectionVisible(activePreview.id, items)) return;
    setActivePreview(null);
  }, [items, activePreview]);

  const handleTogglePreview = (item) => {
    // No fallback: an item with no embedded preview audio gets no play
    // button at all (see KitRow's "unavailable" state below), but guard
    // here too rather than relying only on that button being absent from
    // the DOM - this must do nothing, never error, no matter how it's
    // reached.
    if (!item.has_preview_audio) return;
    if (activePreview?.id === item.id) {
      player.toggle();
    } else {
      setActivePreview({ id: item.id, url: presetsApi.previewUrl(item.id) });
    }
  };

  const commit = (nextFilters, { replace = false } = {}) => {
    setSearchParams(serializeKitsParams(nextFilters), { replace });
  };

  useEffect(() => {
    const myGen = ++genRef.current;
    let alive = true;
    const controller = new AbortController();

    // Nothing to fetch yet: see showFavorites above.
    if (filters.view === "favorites" && authLoading) {
      setPhase("loading");
      return () => {};
    }

    if (hasLoadedOnce.current) setRefetching(true);
    else setPhase("loading");
    setPage(1);

    const request = showFavorites
      ? presetsApi.listFavorites({ page: 1, page_size: PAGE_SIZE }, { signal: controller.signal })
      : presetsApi.list({ ...listFilters, page: 1, page_size: PAGE_SIZE }, { signal: controller.signal });

    request
      .then((r) => {
        if (!alive || genRef.current !== myGen) return;
        const list = r?.data?.items;
        const safeItems = Array.isArray(list)
          ? list.filter((it) => it && typeof it === "object")
          : [];
        const safeTotal = typeof r?.data?.total === "number" ? r.data.total : safeItems.length;
        setItems(safeItems);
        setTotal(safeTotal);
        setPhase("ready");
        hasLoadedOnce.current = true;

        // Analytics (design section 10.8): cheap, no PII, tells the owner
        // which tempos get hunted and how often filters land on nothing.
        trackEvent("kits_browse", {
          genre: filters.genre,
          bpm: filters.bpm,
          tolerance: filters.tolerance,
          half_double: filters.half_double,
          sort: filters.sort,
          has_query: Boolean(filters.q),
          view: filters.view || "all",
          total: safeTotal,
        });
        if (safeItems.length === 0 && isAnyFilterActive(filters)) {
          trackEvent("kits_empty_result", {
            genre: filters.genre,
            bpm: filters.bpm,
            tolerance: filters.tolerance,
            half_double: filters.half_double,
            has_query: Boolean(filters.q),
          });
        }
      })
      .catch((err) => {
        if (!alive || genRef.current !== myGen) return;
        if (err?.code === "ERR_CANCELED") return; // superseded, not a real failure
        setPhase("error");
      })
      .finally(() => {
        if (alive && genRef.current === myGen) setRefetching(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [filters, retryTick, showFavorites, authLoading]);

  const loadMore = () => {
    const myGen = genRef.current;
    const nextPage = page + 1;
    setLoadingMore(true);
    (showFavorites
      ? presetsApi.listFavorites({ page: nextPage, page_size: PAGE_SIZE })
      : presetsApi.list({ ...listFilters, page: nextPage, page_size: PAGE_SIZE })
    )
      .then((r) => {
        if (genRef.current !== myGen) return; // filters changed mid-flight
        const list = r?.data?.items;
        const safeItems = Array.isArray(list)
          ? list.filter((it) => it && typeof it === "object")
          : [];
        setItems((prev) => [...prev, ...safeItems]);
        if (typeof r?.data?.total === "number") setTotal(r.data.total);
        setPage(nextPage);
      })
      .catch(() => {
        // Leave items/page untouched; the button just stays available to retry.
      })
      .finally(() => {
        if (genRef.current === myGen) setLoadingMore(false);
      });
  };

  const retry = () => setRetryTick((t) => t + 1);

  const anyFilter = isAnyFilterActive(filters);
  // null while the count isn't trustworthy: first load or a failed fetch
  // (ResultsLine renders a skeleton bar instead of a fabricated "0 kits").
  const summary = phase === "ready" ? summarizeFilters(filters, total, items.length) : null;
  const ratedOnPage = items.filter((it) => (it.rating_count ?? 0) > 0).length;
  const settlingNote =
    filters.sort === "rating" && items.length > 0 && ratedOnPage < 5
      ? "Few kits have ratings yet, so this order is still settling."
      : null;

  const rowColors = useMemo(() => assignRowColors(items.map((it) => it.id)), [items]);

  const clearAllFilters = () =>
    commit({ ...filters, genre: "", bpm: null, tolerance: 5, half_double: false, q: "" });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-clash text-3xl font-bold sm:text-4xl">Drum kits and patterns library</h1>
          <Link
            to="/kits/upload"
            className="btn-primary px-5 py-2.5 rounded text-sm font-semibold whitespace-nowrap"
          >
            Share a kit
          </Link>
        </div>
        {/* Same copy as KITS_INTRO in scripts/docs-lib.mjs, the prerendered
            head Google reads first. Keep in sync. */}
        <p className="mt-4 max-w-2xl text-base text-white/70">
          Drum kits and patterns made in TrapMan and shared by the producers who use it. Play any
          loop right here, filter by genre and BPM, then download it free and open it in TrapMan.
        </p>

        {user && (
          <div className="mt-8">
            <ViewTabs
              view={filters.view}
              onChange={(view) => commit({ ...defaultFilters(), view })}
            />
          </div>
        )}

        {/* Genre, BPM and search are dropped in the favourites view rather
            than disabled: GET /api/presets/favorites takes page and page_size
            and nothing else, so a control that looked live but changed nothing
            would be worse than no control. */}
        {!showFavorites && (
          <div className="mt-8 space-y-3 sm:space-y-0">
            <GenreChips genre={filters.genre} onChange={(genre) => commit({ ...filters, genre })} />

            <div className="flex flex-wrap items-center gap-3 sm:mt-3">
              <BpmControl
                bpm={filters.bpm}
                tolerance={filters.tolerance}
                halfDouble={filters.half_double}
                onBpmChange={(bpm) => commit({ ...filters, bpm }, { replace: true })}
                onToleranceChange={(tolerance) => commit({ ...filters, tolerance })}
                onHalfDoubleChange={(half_double) => commit({ ...filters, half_double })}
              />
              <SearchField
                q={filters.q}
                onChange={(q) => commit({ ...filters, q }, { replace: true })}
              />
            </div>
          </div>
        )}

        <ResultsLine
          summary={summary}
          clearVisible={anyFilter}
          onClear={clearAllFilters}
          sort={filters.sort}
          onSortChange={(sort) => commit({ ...filters, sort })}
          sortVisible={!showFavorites}
        />
        {settlingNote && <p className="mt-2 text-xs text-[#A1A1AA]">{settlingNote}</p>}

        {phase === "loading" && <KitsSkeleton />}

        {phase === "error" && (
          <KitsEmptyState
            heading="Could not load kits."
            body="Check your connection and try again."
            actions={[{ label: "Try again", onClick: retry }]}
          />
        )}

        {phase === "ready" && items.length === 0 && anyFilter && (
          <KitsEmptyState
            heading="No kits match these filters."
            body={noResultsBody(filters)}
            actions={emptyStateActions(filters).map((a) => ({
              label: a.label,
              onClick: () => commit(a.next),
            }))}
          />
        )}

        {phase === "ready" && items.length === 0 && !anyFilter && showFavorites && (
          <KitsEmptyState
            heading="No favourites yet."
            body="Tap the heart on a kit and it lands here."
            actions={[{ label: "Browse all kits", onClick: () => commit(defaultFilters()) }]}
          />
        )}

        {phase === "ready" && items.length === 0 && !anyFilter && !showFavorites && (
          <KitsEmptyState
            heading="No kits yet."
            body="Be the first to share a kit and its pattern."
            actions={[{ label: "Share a kit", to: "/kits/upload" }]}
          />
        )}

        {phase === "ready" && items.length > 0 && (
          <>
            <div
              aria-busy={refetching}
              className={`mt-4 flex flex-col gap-2 transition-opacity ${
                refetching ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              {items.map((it, i) => {
                const isActiveCard = activePreview?.id === it.id;
                // "unavailable" is a finished state, not a failure - see
                // KitRow below - and takes priority over isActiveCard: a
                // row can only ever become the active preview by way of
                // handleTogglePreview, which already refuses a
                // no-audio item, but this keeps the two checks independent
                // rather than relying on that alone.
                const previewState = !it.has_preview_audio
                  ? "unavailable"
                  : !isActiveCard
                  ? "idle"
                  : player.isLoading
                  ? "loading"
                  : player.isPlaying
                  ? "playing"
                  : "idle";
                return (
                  <KitRow
                    key={it.id}
                    item={it}
                    color={rowColors[i]}
                    previewState={previewState}
                    previewError={isActiveCard ? player.error : ""}
                    onTogglePreview={handleTogglePreview}
                    getPosition={player.getPosition}
                    getDuration={player.getDuration}
                  />
                );
              })}
            </div>
            {items.length < total && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-5 py-2.5 rounded border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : "Show more kits"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// One Splice-style row: play button, name and details on the left, the
// preview's waveform on the right in the kit's own colour.
function KitRow({ item, color, previewState, previewError, onTogglePreview, getPosition, getDuration }) {
  const isLoading = previewState === "loading";
  const isPlaying = previewState === "playing";
  // Finished state, not a failure: this kit was shared without an embedded
  // preview (an older plugin build, or a kit shared before this feature
  // existed). No fallback render, so no dead play button - just a disabled,
  // honestly-labelled control in its place.
  const isUnavailable = previewState === "unavailable";
  // Bumped after a download from this row, so the count reacts without a
  // refetch of the whole page.
  const [downloads, setDownloads] = useState(item.downloads ?? 0);

  // The row itself is a react-router Link to the detail page - the preview
  // button and the waveform must never navigate, only toggle playback. Both
  // calls are needed: stopPropagation keeps the click from ever reaching the
  // Link's own handler, preventDefault covers the native <a> activation.
  // onTogglePreview itself also refuses a no-audio item.
  const handlePreviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onTogglePreview(item);
  };

  const previewLabel = isUnavailable
    ? `No preview audio for ${item.title}`
    : isPlaying
    ? `Pause preview of ${item.title}`
    : isLoading
    ? `Loading preview of ${item.title}`
    : `Play preview of ${item.title}`;

  return (
    <Link
      to={`/kits/${item.id}`}
      className="feature-card rounded-lg px-3 py-3 sm:px-4 sm:py-2 no-underline flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5"
    >
      <div className="flex min-w-0 items-center gap-3 sm:w-80 sm:shrink-0">
        <PreviewKnob
          state={previewState}
          color={color}
          label={previewLabel}
          title={previewError || (isUnavailable ? "No preview audio" : undefined)}
          onClick={handlePreviewClick}
          getPosition={getPosition}
          getDuration={getDuration}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-semibold leading-6 text-white">{item.title}</div>
            {/* FavoriteButton doesn't know it's nested inside this row's
                Link, so the wrapping span carries the preventDefault/
                stopPropagation the Link requires. */}
            <span
              className="shrink-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FavoriteButton presetId={item.id} isFavorited={item.is_favorited} size="md" />
            </span>
          </div>
          {/* Genre/BPM and the read-only rating share a line, and the stats
              take the next: three lines instead of four keeps the row no
              taller than its waveform, so more kits fit on screen. The
              rating is always shown: at zero RatingStars draws outline stars
              with no text, so the "Best rated" sort reads as legible on
              every row. */}
          <div className="flex items-center justify-between gap-2 text-sm leading-5 text-[#A1A1AA]">
            <span className="shrink-0">
              {item.genre} &middot; {Math.round(item.bpm || 0)} BPM
            </span>
            {/* Rated from the row itself. Same Link trap as FavoriteButton
                above: the span swallows the click before it navigates. */}
            <span
              className="min-w-0 overflow-hidden"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <RatingStars
                presetId={item.id}
                ratingAvg={item.rating_avg}
                ratingCount={item.rating_count}
                myRating={item.my_rating}
                interactive
                compact
                size="sm"
              />
            </span>
          </div>
          <div className="truncate text-xs leading-4 text-[#71717A]">
            {item.lane_count ?? 0} lanes &middot; {item.step_count ?? 0} steps &middot;{" "}
            {downloads} {downloads === 1 ? "download" : "downloads"}
          </div>
        </div>
      </div>

      <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1 sm:gap-3">
        <div
          className={`h-16 min-w-0 flex-1 sm:h-[72px] ${isUnavailable ? "" : "cursor-pointer"}`}
          onClick={handlePreviewClick}
        >
          <KitWaveform
            color={color}
            url={item.has_preview_audio ? presetsApi.previewUrl(item.id) : null}
            numBars={item.num_bars}
            active={isPlaying}
            getPosition={getPosition}
            getDuration={getDuration}
          />
        </div>
        <div className="flex shrink-0 items-center">
          <KitDownloadButton item={item} onDownloaded={() => setDownloads((n) => n + 1)} />
          <CopyLinkButton item={item} />
        </div>
      </div>
    </Link>
  );
}

// Download, at the right end of a row. Drawn like the plugin's share glyph
// in its preset browser: a bare white outline icon, dim until hovered, no
// button chrome. Signed out, it routes to /login like the kit page does.
function KitDownloadButton({ item, onDownloaded }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onClick = async (e) => {
    // Inside the row's Link: download, never navigate to the kit page.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await downloadKit(item);
      onDownloaded?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Download failed.");
    } finally {
      setBusy(false);
    }
  };

  const label = user ? `Download ${item.title}` : `Log in to download ${item.title}`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={label}
      title={error || label}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
        error ? "opacity-100 text-red-400" : "opacity-50 hover:opacity-95 active:opacity-80"
      } disabled:cursor-wait`}
      style={{ filter: "drop-shadow(0 0 1.5px rgba(255, 255, 255, 0.3))" }}
    >
      {busy ? <Loader2 size={18} strokeWidth={1.75} className="animate-spin" /> : <Download size={19} strokeWidth={1.6} />}
    </button>
  );
}
